import { useSocketContext } from '@/context/SocketProvider'
import LiveSupportBanner from '@/components/molecules/live-support-banner'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatTicketUpdateCount } from '@/lib/ticket-update-badge'
import { useChatStore } from '@/store/chat.store'
import { usePersistStore } from '@/store/persist.store'
import { useSocketStore } from '@/store/socket.store'
import { FileText, Maximize2, MessageCircle, Minimize2, MinusIcon, RotateCcw, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Fragment, useEffect, useState } from 'react'

function ChatModalHeader({ onOpenNewChat }: { onOpenNewChat?: () => void }) {
  const { closeChat, isClosingChat } = useSocketContext()
  const pendingNewTopicAck = useSocketStore(state => state.pendingNewTopicAck)
  const t = useTranslations('chatHeader')
  const tTickets = useTranslations('tickets')
  const isAuthSuccess = useSocketStore(state => state.isAuthSuccess)
  const activeTab = useChatStore(state => state.activeTab)
  const setActiveTab = useChatStore(state => state.setActiveTab)
  const ticketUpdateCount = useChatStore(state => state.ticketUpdateCount)
  const toggleChatIframeShown = useChatStore(state => state.toggleChatIframeShown)
  const isExpanded = useChatStore(state => state.isExpanded)
  const isSecondChatShown = useChatStore(state => state.isSecondChatShown)
  const comm100Url = useChatStore(state => state.comm100Url)
  const parentWidth = useChatStore(state => state.parentWidth)
  const setIsExpanded = useChatStore(state => state.setIsExpanded)
  const setSelectedSection = useChatStore(state => state.setSelectedSection)
  const setMessages = useChatStore(state => state.setMessages)
  const setIsChatEnded = useChatStore(state => state.setIsChatEnded)
  const isChatEnded = useChatStore(state => state.isChatEnded)
  const sessionId = usePersistStore(state => state.sessionId)
  const setSessionId = usePersistStore(state => state.setSessionId)
  const setIsSecondChatShown = useChatStore(state => state.setIsSecondChatShown)
  const [isCloseConfirmShown, setIsCloseConfirmShown] = useState(false)

  const buttonClass =
    'flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-[var(--widget-header-foreground)] transition-colors hover:bg-white/10 hover:text-[var(--widget-header-foreground)] focus-visible:outline-current disabled:cursor-wait disabled:opacity-50'

  const handleMaximize = () => {
    setIsExpanded(true)

    let width = null
    let height = 'calc(100dvh - 96px)'

    if (parentWidth) {
      if (parentWidth >= 1800) {
        width = '900px'
      } else if (parentWidth >= 1400) {
        width = '800px'
      } else if (parentWidth >= 1000) {
        width = '700px'
      } else if (parentWidth >= 700) {
        width = '600px'
      } else if (parentWidth <= 600) {
        height = '100%'
        width = '100%'
      }
    } else {
      window.parent.postMessage({ type: 'GET_PARENT_WIDTH' }, '*')
    }

    window.parent.postMessage(
      {
        type: 'RESIZE_IFRAME',
        payload: {
          width,
          height
        }
      },
      '*'
    )
  }

  const handleMinimize = () => {
    setIsExpanded(false)

    let width = null

    if (parentWidth) {
      if (parentWidth > 600) {
        width = '430px'
      } else {
        width = '100%'
      }
    }

    window.parent.postMessage(
      {
        type: 'RESIZE_IFRAME',
        payload: {
          width,
          height: 'min(750px, calc(100dvh - 96px))'
        }
      },
      '*'
    )
  }

  // Ending a live session is irreversible, so ask first. Without a session, or
  // once it has already ended, there is nothing to lose and the widget closes
  // directly.
  const requestCloseChat = () => {
    if (sessionId && !isChatEnded) setIsCloseConfirmShown(true)
    else closeChat()
  }

  const confirmCloseChat = () => {
    setIsCloseConfirmShown(false)
    closeChat()
  }

  useEffect(() => {
    if (!isCloseConfirmShown) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsCloseConfirmShown(false)
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isCloseConfirmShown])

  const toggleChangeChat = () => {
    setIsSecondChatShown(!isSecondChatShown)
  }

  const handleOpenSectionSelect = () => {
    setSelectedSection(null)
    setMessages([])
    setSessionId(null)
    setIsChatEnded(false)
    setTimeout(() => {
      setIsSecondChatShown(false)
    }, 500)
  }

  type ButtonData = {
    icon?: React.ReactNode
    tooltip?: string
    onClick?: () => void
    disabled?: boolean
    key?: string
    renderNode?: React.ReactNode
  }

  const buttonData: ButtonData[] = [
    comm100Url &&
      isSecondChatShown && {
        key: 'switch-chat-button',
        renderNode: (
          <Button
            onClick={toggleChangeChat}
            size='sm'
            variant='ghost'
            className='border border-white/20 text-xs text-[var(--widget-header-foreground)] hover:bg-white/10 hover:text-[var(--widget-header-foreground)]'
          >
            {t('switchToAi')}
          </Button>
        )
      },
    {
      icon: <RotateCcw className='size-4' />,
      tooltip: t('selectNewTopic'),
      disabled: isClosingChat || !!pendingNewTopicAck,
      onClick: () => {
        const openNewChat = onOpenNewChat ?? handleOpenSectionSelect
        setActiveTab('chat')
        openNewChat()
      }
    },
    !isExpanded &&
      parentWidth > 600 && {
        icon: <Maximize2 className='size-4' />,
        tooltip: t('expandWindow'),
        onClick: handleMaximize
      },
    isExpanded &&
      parentWidth > 600 && {
        icon: <Minimize2 className='size-4' />,
        tooltip: t('restoreWindow'),
        onClick: handleMinimize
      },
    {
      icon: <MinusIcon className='size-4' />,
      tooltip: t('minimizeWindow'),
      onClick: toggleChatIframeShown
    },
    {
      icon: <X className='size-4' />,
      tooltip: t('closeChat'),
      onClick: requestCloseChat,
      disabled: isClosingChat || !!pendingNewTopicAck
    }
  ].filter(Boolean)

  useEffect(() => {
    if (parentWidth && parentWidth <= 600) {
      setIsExpanded(false)
      window.parent.postMessage(
        {
          type: 'RESIZE_IFRAME',
          payload: {
            width: '100%',
            height: '100%'
          }
        },
        '*'
      )
    } else {
      handleMinimize()
    }
  }, [parentWidth])

  return (
    <>
      <header className='relative flex min-h-[68px] items-center justify-between gap-2 overflow-hidden bg-[var(--widget-header)] px-4 py-3.5 text-[var(--widget-header-foreground)]'>
        <svg
          aria-hidden='true'
          className='pointer-events-none absolute inset-0 size-full fill-none stroke-current opacity-15'
          preserveAspectRatio='none'
          viewBox='0 0 400 120'
        >
          <path d='M-20,90 C80,30 180,120 280,50 C340,10 390,70 440,40' strokeWidth='1.5' />
          <path d='M-10,40 C90,110 170,20 290,90 C360,130 390,30 430,60' strokeWidth='1.2' />
          <path d='M20,110 C120,40 230,100 320,30 C370,-10 410,50 440,20' />
          <circle cx='360' cy='25' r='45' />
        </svg>
        <div className='relative flex min-w-0 items-center gap-2'>
          <LiveSupportBanner />
        </div>
        <div className='relative flex shrink-0 items-center gap-1'>
          {buttonData.map(button =>
            button.renderNode ? (
              <Fragment key={button.key}>{button.renderNode}</Fragment>
            ) : (
              <Tooltip key={button.tooltip}>
                <TooltipTrigger asChild>
                  <button
                    disabled={button.disabled}
                    type='button'
                    aria-label={button.tooltip}
                    onClick={button.onClick}
                    className={buttonClass}
                  >
                    {button.icon}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{button.tooltip}</TooltipContent>
              </Tooltip>
            )
          )}
        </div>
      </header>
      {isAuthSuccess && (
        <nav aria-label={tTickets('tabChat')} className='bg-background flex shrink-0 border-b'>
          {(
            [
              { key: 'chat', label: tTickets('tabChat'), icon: MessageCircle },
              { key: 'tickets', label: tTickets('title'), icon: FileText }
            ] as const
          ).map(tab => (
            <button
              key={tab.key}
              type='button'
              aria-current={activeTab === tab.key ? 'page' : undefined}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'relative flex min-w-0 flex-1 cursor-pointer items-center justify-center gap-2 px-4 py-3 text-sm transition-colors',
                activeTab === tab.key
                  ? 'text-primary font-semibold'
                  : 'text-muted-foreground hover:text-foreground font-medium'
              )}
            >
              <tab.icon className='size-[18px] shrink-0' />
              <span>{tab.label}</span>
              {tab.key === 'tickets' && ticketUpdateCount > 0 && (
                <span className='bg-primary/10 text-primary rounded-full px-1.5 text-[10px] font-bold'>
                  {formatTicketUpdateCount(ticketUpdateCount)}
                </span>
              )}
              {activeTab === tab.key && (
                <span className='bg-primary absolute inset-x-6 bottom-0 h-[3px] rounded-full' />
              )}
            </button>
          ))}
        </nav>
      )}
      {isCloseConfirmShown && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6'
          onClick={() => setIsCloseConfirmShown(false)}
        >
          <div
            role='alertdialog'
            aria-modal='true'
            aria-labelledby='close-chat-confirm-title'
            aria-describedby='close-chat-confirm-description'
            className='bg-background text-foreground w-full max-w-xs rounded-2xl p-5 shadow-lg'
            onClick={event => event.stopPropagation()}
          >
            <h2 id='close-chat-confirm-title' className='text-base font-semibold'>
              {t('closeConfirmTitle')}
            </h2>
            <p id='close-chat-confirm-description' className='text-muted-foreground mt-2 text-sm'>
              {t('closeConfirmDescription')}
            </p>
            <div className='mt-5 flex flex-col gap-2'>
              <Button
                autoFocus
                variant='outline'
                className='h-auto min-h-11 whitespace-normal'
                onClick={() => setIsCloseConfirmShown(false)}
              >
                {t('closeConfirmCancel')}
              </Button>
              <Button variant='destructive' className='h-auto min-h-11 whitespace-normal' onClick={confirmCloseChat}>
                {t('closeConfirmEnd')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ChatModalHeader
